import React, { useEffect, useState } from 'react';
import { Grid } from '@mui/material';
import './LoyaltySection.scss';
import {
  LoyaltyArticle,
  RestaurantArticlesWithDiscount,
} from '../../../interfaces/loyality.interface';
import { useConfig } from '../../../context/ConfigContext';
import { loyalityApi } from '../../../services/loyalityApi';
import { useNavigate } from 'react-router-dom';
import { Restaurant } from '../../../interfaces/Catalogue';
import { UserInfo } from '../../../interfaces/user';
import LoyaltyHeader from '../LoyaltyHeader/LoyaltyHeader';
import PanierDiscountCard from '../PanierDiscountCard/PanierDiscountCard';
import ArticlesDiscountSection from '../ArticlesDiscountSection/ArticlesDiscountSection';
import { useSelector, useDispatch } from 'react-redux';
import { transformToLoyaltyItems } from '../../../utils/loyaltyUtils';
import SectionHeader from '../ArticlesDiscountSection/SectionHeader/SectionHeader';
import { LoyaltyArticlesList } from '../ArticlesDiscountSection/LoyaltyArticlesList/LoyaltyArticlesList';
import { setSelectedRestaurant } from '../../../store/actions/actions';
import RestaurantSelectionModal from '../../common/RestaurantSelectionModal/RestaurantSelectionModal';

// ─── Replace with your actual restaurant-picker modal import ───
// import RestaurantPickerModal from '../../shared/Modals/RestaurantPickerModal/RestaurantPickerModal';

const LoyaltySection: React.FC = () => {
  const isPhone = window.innerWidth <= 768;
  const { config } = useConfig();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const userInfo: UserInfo = useSelector((state: any) => state.auth.userInfo);
  const selectedRestaurant: Restaurant = useSelector(
    (state: any) => state.shop.selectedRestaurant,
  );
  const restaurants: Restaurant[] = useSelector(
    (state: any) => state.shop.shops,
  );

  const [restaurantArticles, setRestaurantArticles] =
    useState<RestaurantArticlesWithDiscount | null>(null);
  const [loading, setLoading] = useState(false);
  const [maxPoints, setMaxPoints] = useState(0);
  const [userProgress, setUserProgress] = useState(0);
  const [missingPoints, setMissingPoints] = useState(0);
  const [loyaltyArticles, setLoyaltyArticles] = useState<LoyaltyArticle[] | null>([]);
  const [showRestaurantModal, setShowRestaurantModal] = useState(false);

  const currentUserPoints = restaurantArticles?.loyaltyClient?.loyaltyPoints || 0;

  const fetchArticles = async () => {
    try {
      setLoading(true);
      const response = await loyalityApi.getRestaurantArticlesWithDiscount(
        selectedRestaurant?.id?.toString(),
        userInfo?.qrCodeValue,
      );
      const { result } = response.data;

      if (result?.cartRewardRule) {
        result.cartRewardRule.rewardDiscountType =
          result.cartRewardRule.rewardDiscountType ?? 0;
      }

      setRestaurantArticles(result);

      const userLoyaltyPoints = result?.loyaltyClient?.loyaltyPoints || 0;

      if (result?.articleRewardRules?.length) {
        const lastArticle =
          result.articleRewardRules[result.articleRewardRules.length - 1];

        setMaxPoints(lastArticle?.rewardTotalPtRequired || 0);

        const pct =
          (userLoyaltyPoints / (lastArticle?.rewardTotalPtRequired || 1)) * 100;
        setUserProgress(pct > 100 ? 100 : pct);

        const firstBig = result.articleRewardRules.find(
          (r) => r.rewardTotalPtRequired > userLoyaltyPoints,
        );
        setMissingPoints(
          Math.max(0, (firstBig?.rewardTotalPtRequired || 0) - userLoyaltyPoints),
        );

        setLoyaltyArticles(
          transformToLoyaltyItems(result.articleRewardRules, userLoyaltyPoints),
        );
      } else {
        setLoyaltyArticles([]);
      }
    } catch (error) {
      setLoyaltyArticles([]);
      setRestaurantArticles(null);
      console.error('Error fetching articles:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedRestaurant?.id) fetchArticles();
  }, [selectedRestaurant, userInfo?.qrCodeValue]);

  const onPressViewAll = () => {
    navigate(`/${selectedRestaurant?.id}/restaurant-fiofid`);
  };

  const handleSelectRestaurant = (restaurant: Restaurant) => {
    dispatch(setSelectedRestaurant(restaurant));
    setShowRestaurantModal(false);
  };

  const hasPanierDiscount =
    config?.showFidelity && restaurantArticles?.cartRewardRule;
  const hasArticlesDiscount =
    config?.showFidelity &&
    !restaurantArticles?.cartRewardRule &&
    loyaltyArticles &&
    loyaltyArticles.length > 0;

  return (
    <div className="loyalty-container">

      {/* ── Restaurant picker modal ── */}

      <RestaurantSelectionModal
        visible={showRestaurantModal}
        onSelect={handleSelectRestaurant}
        onClose={() => setShowRestaurantModal(false)}
      />


      <Grid container spacing={2} alignItems="stretch">

        {/* ── LoyaltyHeader ── */}
        <Grid
          item
          xs={12}
          md={hasPanierDiscount || hasArticlesDiscount ? 6 : 12}
          style={{ display: 'flex', flexDirection: 'column' }}
        >
          <LoyaltyHeader
            userInfo={userInfo}
            userPoints={currentUserPoints}
            restaurantName={selectedRestaurant?.nom}
            loading={loading}
            onChangeRestaurant={
              restaurants?.length > 1
                ? () => setShowRestaurantModal(true)
                : undefined
            }
          />
        </Grid>

        {/* ── Panier discount ── */}
        {hasPanierDiscount && (
          <Grid
            item
            xs={12}
            md={6}
            style={{ display: 'flex', flexDirection: 'column' }}
          >
            <PanierDiscountCard
              restaurantArticles={restaurantArticles}
              currentUserPoints={currentUserPoints}
              onPress={onPressViewAll}
            />
          </Grid>
        )}

        {/* ── Articles discount ── */}
        {hasArticlesDiscount && (
          <Grid
            item
            xs={12}
            md={6}
            style={{ display: 'flex', flexDirection: 'column' }}
          >
            <ArticlesDiscountSection
              restaurant={selectedRestaurant}
              userInfo={userInfo}
              loyaltyArticles={loyaltyArticles}
              currentUserPoints={currentUserPoints}
              maxPoints={maxPoints}
              userProgress={userProgress}
              missingPoints={missingPoints}
              onPressViewAll={onPressViewAll}
            />
          </Grid>
        )}
      </Grid>

      {hasArticlesDiscount && (
        <div className="articles-section">
          <SectionHeader
            title="Récompenses disponibles"
            iconName="redeem"
            onPressSeeAll={onPressViewAll}
            showSeeAll
          />
          <LoyaltyArticlesList
            rewards={loyaltyArticles.slice(0, 4)}
            itemsPerRow={4}
            scrollDirection={isPhone ? 'horizontal' : 'vertical'}
            restaurant={selectedRestaurant}
            onPressViewAll={onPressViewAll}
          />
        </div>
      )}
    </div>
  );
};

export default LoyaltySection;